/**
 * Created by jonas on 2023-02-13.
 */
import { gsap } from 'gsap';
import { AbstractSpine } from '../../core/component/AbstractSpine';
import { Beheaded } from '../Beheaded';
import { LevelBar } from './LevelBar';
import { SlotKeypad } from '@nolimitcity/slot-keypad/bin/SlotKeypad';
import { ArrayHelper } from '../../core/utils/ArrayHelper';
import { SpinePlayer } from '../../core/displayobject/SpinePlayer';
import { Point } from 'pixi.js';
import { IDimOrientation, ResizeHelper } from '../../core/utils/ResizeHelper';
import { StageLayer } from '@nolimitcity/slot-launcher/bin/StageLayer';
import { GameSetting } from '../../core/setting/GameSetting';
import { Helper } from '../../core/utils/Helper';
import { MathHelper } from '../../core/utils/MathHelper';
import { GraveSnatchers } from '../Font/GraveSnatchers';
import { TranslationTexts } from '../config/translate/TranslationTexts';
import { PosterManager } from '../modules/Poster/PosterManager';
import { SymbolData } from '../../core/reel/symbol/config/SymbolData';

export class CollectorBar extends PIXI.Container implements StageLayer {
    private addSpinsText1: PIXI.Text;
    private addSpinsText2: PIXI.Text;

    private levelBar: LevelBar;
    private _levelCharacter: SpinePlayer[] = [];
    private _levelCharContainer: PIXI.Container;

    private animatedTl: gsap.core.Timeline;

    protected static readonly dimObj: IDimOrientation = {
        landscape: {
            scale: new Point(1, 1),
            position: new Point(365, 646),
        },
        portrait: {
            scale: new Point(1, 1),
            position: new Point(365, 646),
        },
    };

    public get lvlTresholds(): number[] {
        return this.levelBar.lvlTresholds;
    }

    public get collectedValue(): number {
        return this.levelBar.collectedValue;
    }

    public get currentLevel(): number {
        return this.levelBar.currentBarLevel;
    }

    public thresHoldLevelValue(lvl: number): number {
        return this.levelBar.currentBarLevel;
    }

    public indicatorTarget2(indx: number) {
        return this._levelCharContainer.children[[0, 1, 3, 4, 2][indx]];
    }

    public indicatorTarget(indx: number) {
        return this._levelCharContainer.children[indx];
    }

    public indicatorPlaces(indx: number) {
        return [1, 2, 5, 3, 4][indx];
    }

    private fetchLevelIndicator(level: number): SpinePlayer {
        if (level == 4) return this._levelCharacter[2];
        else if (level >= 2) return this._levelCharacter[level + 1];
        else return this._levelCharacter[level];
    }

    constructor(thresholds: number[]) {
        super();
        this.init(thresholds);
    }

    private init(thresholds: number[]) {
        //level bar
        this.levelBar = new LevelBar(thresholds);
        this.levelBar.name = 'LEVEL_BAR';
        this.levelBar.position.set(-280, 0);

        this.addChild(this.levelBar);

        this._levelCharContainer = this.addChild(new PIXI.Container());
        this._levelCharContainer.position.set(-2, -30);

        for (let i = 0; i < 5; i++) {
            const hSymbol: SpinePlayer = this._levelCharContainer.addChild(new SpinePlayer('cauldronSym', true));
            hSymbol.play({ animation: 'deactive', skinType: 'H' + this.indicatorPlaces(i), showOnEnd: true });
            hSymbol.position.set(-240 + i * 120, i == 2 ? -15 : 0);
            this._levelCharacter.push(hSymbol);
        }

        this.createTexts();

        this.addChild(new PIXI.Container());

        this.onResize();
    }

    private createTexts(): void {
        this.addSpinsText1 = this.addChild(new PIXI.Text(`+2`));
        this.addSpinsText1.style = {
            fontFamily: GraveSnatchers.FAMILY,
            fontSize: '30px',
            stroke: '#000000',
            strokeThickness: 10,
            fill: '#265cff',
            dropShadow: true,
            dropShadowColor: '#000000',
            dropShadowAlpha: 0.7,
            dropShadowAngle: 0.6,
            dropShadowDistance: 8,
            padding: 20,
            lineJoin: 'round',
        };
        this.addSpinsText1.alpha = 0;
        this.addSpinsText1.anchor.set(0.5, 0.5);

        this.addSpinsText2 = this.addChild(new PIXI.Text(`${TranslationTexts.FREESPINS}`));
        this.addSpinsText2.style = {
            fontFamily: GraveSnatchers.FAMILY,
            fontSize: '15px',
            stroke: '#000000',
            strokeThickness: 10,
            fill: '#265cff',
            dropShadow: true,
            dropShadowColor: '#000000',
            dropShadowAlpha: 0.7,
            dropShadowAngle: 0.6,
            dropShadowDistance: 8,
            padding: 20,
            lineJoin: 'round',
        };
        this.addSpinsText2.alpha = 0;
        this.addSpinsText2.anchor.set(0.5, 0.5);
        this.addSpinsText2.pivot.set(0, -30);
    }

    public moveLvlUpSymToBar(level: number,isNaturalSfs:boolean): gsap.core.Timeline {

        const tl = gsap.timeline({onComplete:() =>{
            sym.destroy();
            this.addSpinsView(level,undefined, isNaturalSfs);
            this.setLevel(level);
        }});

        const sym = this.addChild(new SpinePlayer("M_H" + (6-level), true));
        sym.name = 'symbolLevelUpCharged';

        const targetIndicatorPos = MathHelper.pointAdd(
            this.fetchLevelIndicator(level - 1).position,
            this._levelCharContainer.position
        );

        tl.fromTo(sym, { x: 0, y: 0, alpha: 0 }, { y: -360, alpha: 1, duration: 0.5 }, 0);
        tl.fromTo(sym.scale, { x: 0.7, y: 0.7 }, { y: 1, x: 1, duration: 0.5 }, 0);

        tl.add(() => {
            sym.play({
                animation: `charged`,
                showOnEnd: true,
                onComplete: () => {
                    sym.play({ animation: `charged_loop`, loop: true });
                },
            });
        });

        tl.fromTo(sym.scale, { x: 1, y: 1 }, { y: 1.25, x: 1.25, duration: 0.15 });
        tl.fromTo(sym.scale, { x: 1.25, y: 1.25 }, { y: 1, x: 1, duration: 0.15 });

        tl.to(sym, { x: targetIndicatorPos.x, y: targetIndicatorPos.y, duration: 0.75, ease: 'Back.easeIn' }, '+=' + 0.2);
        tl.to(sym.scale, { x: 0.25, y: 0.25, duration: 0.75, ease: 'Back.easeIn' }, '<');

        return tl;
    }

    public forceReset() {
        this.levelBar.forceReset();
        this._levelCharacter.forEach((char, indx) => {
            if (char.isPlaying) {
                this._levelCharacter[indx].play({
                    animation: 'deactive',
                    skinType: 'H' + this.indicatorPlaces(indx),
                    showOnEnd: true,
                });
            }
        });
        // this.setLevel(0,true,true);
    }

    public resetAnimationLevel(level: number) {
        this.levelBar.reset(level);
        this.setLevel(level, true, true);
    }

    public addHeads(headType: string) {
        this.levelBar.addHeadToBeCollected(headType);
    }

    public setValue(totalValue: number, nextLevel: number) {
        this.levelBar.animateTo(totalValue, 0.5, nextLevel);
    }

    public updateTheBar() {
        this.levelBar.UpgradeToNextLevel();
    }

    private tlText:gsap.core.Timeline;
    public addSpinsView(level:number,durr:number = 0.5,isNaturalSfs:boolean): gsap.core.Timeline {
        this.tlText = Helper.setTimeline(this.tlText);

        this.addSpinsText1.text = isNaturalSfs ? "+6" :"+2";

        const pos = this.fetchLevelIndicator(level-1).position;
        const posX = pos.x;
        const posY = pos.y - 50;

        this.tlText.fromTo(
            [this.addSpinsText1, this.addSpinsText2],
            { x: posX, y: posY, alpha: 0 },
            { y: '-=50', duration: durr, alpha: 1 },
            0
        );
        this.tlText.to([this.addSpinsText1, this.addSpinsText2], { alpha: 0, duration: durr * 0.4 }, durr);

        return this.tlText;
    }

    activeOnlyIndicator(level: number): void {
        const char = this.fetchLevelIndicator(level - 1);
        char.play({
            animation: 'active',
            skinType: 'H' + level,
            showOnEnd: true,
            onComplete: () => {
                char.play({ animation: 'active_loop', skinType: 'H' + level, loop: true });
            },
        });
    }

    public setLevel(level: number, includeBar: boolean = false, forceValueReset: boolean = false) {
        if (includeBar) {
            this.levelBar.setLevel(level);
            this.levelBar.updateText(forceValueReset ? 0 : undefined);
        }

        if(forceValueReset){
            this._levelCharacter.forEach((char, indx) => {
                if (char.isPlaying) {
                    if (GameSetting.isBoostedBet) {
                        if (indx) {
                            this._levelCharacter[indx].play({
                                animation: 'deactive',
                                skinType: 'H' + this.indicatorPlaces(indx),
                                showOnEnd: true,
                            });
                        }
                    } else {
                        this._levelCharacter[indx].play({
                            animation: 'deactive',
                            skinType: 'H' + this.indicatorPlaces(indx),
                            showOnEnd: true,
                        });
                    }
                }
            });
        }

        if (level) {
            for (let i = 0; i < level; i++) {
                const char = this.fetchLevelIndicator(i);
                if (!char.isPlaying) {
                    char.play({
                        animation: 'active',
                        skinType: 'H' + (i + 1),
                        showOnEnd: true,
                        onComplete: () => {
                            char.play({ animation: 'active_loop', skinType: 'H' + (i + 1), loop: true });
                        },
                    });
                }
            }
        }
    }

    onResize(): void {
        const dim = ResizeHelper.calcDim(CollectorBar.dimObj);
        this.position = dim.position;
        this.scale = dim.scale;
    }
}
